import {
  useGetOneEstimationMethod,
  useGetOneEstimationMethodConfiguration,
} from "#hooks/adapters/useEstimationMethods";
import { useListFlows } from "#hooks/adapters/useFlows";
import { useGetOneNetwork } from "#hooks/adapters/useNetworks";
import { useTableSortingAndPagination } from "#redux/reducers/tableStateReducer";
import { useParams } from "#routers/hooks";
import { NetworkDetailPageParameters } from "#routes/organization/networks/[networkId]/detail";
import {
  useNetworkCalculationFlowHeaders,
  useNetworkCalculationSourceHeaders,
} from "#routes/organization/networks/[networkId]/detail/NetworkCalculationTab/NetworkCalculationTab.helpers";
import type { CalculationsViewFilterAreaType } from "#src/batteries-included-components/FilterAreas/CalculationsFilterAreas";
import { DEFAULT_DATE_RANGES } from "#src/hooks/useDateRange";
import { useStickyState } from "#src/hooks/useStickyState";
import { getMeasurementTypeFromNetworkCalculatorEstimationMethod } from "#src/view-models/asset-groups";
import {
  DataTablePanel,
  HeaderType,
  StorageKeys,
} from "@validereinc/common-components";
import { AssetType, FlowType, SortDirection } from "@validereinc/domain";
import { getYearMonthFromDateRange } from "@validereinc/utilities";
import React from "react";

export const NetworkCalculationInletPanel = ({
  viewConfigStorageKey,
  tableConfigStorageKey,
  tableStateStorageKey,
}: Pick<
  StorageKeys,
  "viewConfigStorageKey" | "tableConfigStorageKey" | "tableStateStorageKey"
>) => {
  const { networkId } = useParams<NetworkDetailPageParameters>();
  const {
    data: assetGroup,
    isLoading: isNetworkLoading,
    isFetching: isNetworkingFetching,
  } = useGetOneNetwork(networkId);
  const [viewFilters] = useStickyState<Partial<CalculationsViewFilterAreaType>>(
    {},
    viewConfigStorageKey
  );
  const filters = {
    // @ts-expect-error TODO: Typing on assetGroup
    id: assetGroup?.network_inlet_ids ?? [],
  };

  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "name",
      sortDirection: SortDirection.ASCENDING,
    },
    filters,
    tableStateStorageKey
  );
  const {
    data: flows,
    isLoading: isFlowsLoading,
    isFetching: isFlowsFetching,
  } = useListFlows(
    {
      page: tableState.page,
      pageSize: tableState.pageSize,
      sortBy: tableState.sortBy,
      sortDirection: tableState.sortDirection,
      filters,
    },
    { enabled: !!filters.id.length }
  );
  const {
    data: configuration,
    isLoading: isConfigurationLoading,
    isFetching: isConfigurationFetching,
  } = useGetOneEstimationMethodConfiguration({
    id: assetGroup?.default_estimation_method_id ?? "",
    assetType: AssetType.ASSET_GROUP,
    yearMonth:
      getYearMonthFromDateRange(
        viewFilters.date_range ?? DEFAULT_DATE_RANGES.currentWholeMonth
      ) ?? "",
  });

  const {
    data: defaultEstimationMethod,
    isLoading: isEstimationMethodLoading,
  } = useGetOneEstimationMethod({
    id: assetGroup?.default_estimation_method_id ?? "",
    entityType: AssetType.ASSET_GROUP,
  });
  const measurementType = defaultEstimationMethod
    ? getMeasurementTypeFromNetworkCalculatorEstimationMethod(
        defaultEstimationMethod.data
      )
    : "";
  const {
    isLoading: isLoadingNetworkCalculationFlowHeaders,
    headers: networkCalculationFlowHeaders,
  } = useNetworkCalculationFlowHeaders();
  const {
    isLoading: isLoadingNetworkCalculationSourceHeaders,
    headers: networkCalculationSourceHeaders,
  } = useNetworkCalculationSourceHeaders(configuration, measurementType);

  const headers: Array<HeaderType<FlowType>> = [
    ...networkCalculationFlowHeaders,
    ...networkCalculationSourceHeaders,
  ];

  const items = flows?.data ?? [];
  const isLoading =
    isNetworkLoading ||
    isFlowsLoading ||
    isConfigurationLoading ||
    isLoadingNetworkCalculationFlowHeaders ||
    isLoadingNetworkCalculationSourceHeaders ||
    isEstimationMethodLoading;
  const isFetching =
    isNetworkingFetching || isFlowsFetching || isConfigurationFetching;

  return (
    <DataTablePanel
      storageKey={tableConfigStorageKey}
      panelProps={{ title: "Inlet" }}
      dataTableProps={{
        headers,
        items: items as FlowType[],
        isLoading,
        isBusy: isFetching,
        getItemId: (item) => item.id,
        sorting: {
          sortBy: tableState.sortBy,
          sortDirection: tableState.sortDirection,
        },
        onSortChange: updateTableState,
        onPaginationChange: updateTableState,
        pagination: {
          page: tableState.page,
          pageSize: tableState.pageSize,
          total: flows?.total_entries,
        },
      }}
    />
  );
};
